import { BetVm } from './BetVm';
import { SportsbookComparisonItemVm } from './SportsbookComparisonItemVm';
import { parseWager } from '../helper/currencyFormatter';
import { decimalToAmerican } from '../helper/converter';
import { deepLinkUrl } from '../constant/constant';

export class ParlayComparisonVm {
    sortedComparisonList: SportsbookComparisonItemVm[]; // Array to hold sorted sportsbooks by highest to lowest 'to win' parlays
    bestSelectedParlay: SportsbookComparisonItemVm | null;
    parlayDeepLink: string | null = null; // Add property to store parlay deep link

    constructor(bets: BetVm[], parlayWager: string) {
        const activeBets = this.filterActiveBets(bets);
        const wager = parseWager(parlayWager);
        this.sortedComparisonList = this.calculateTotalToWin(activeBets, wager);
        this.bestSelectedParlay = this.findBestSelectedParlay(activeBets, this.sortedComparisonList);
        if (this.bestSelectedParlay) {
            this.parlayDeepLink = this.generateParlayDeepLink(activeBets, this.bestSelectedParlay.sportsbookVm.providerBookId) || this.bestSelectedParlay.sportsbookVm.sportsbookClickout;
        }
    }

    private filterActiveBets(bets: BetVm[]): BetVm[] {
        return bets.filter(bet => bet.isActiveLeg);
    }

    private calculateTotalToWin(bets: BetVm[], parlayWager: number): SportsbookComparisonItemVm[] {
        const sportsbookToWin: { [key: string]: SportsbookComparisonItemVm } = {};

        // Initialize toWin for each sportsbook
        bets.forEach((bet) => {
            bet.comparisonBets.forEach((bettingSide) => {
                const sportsbookKey = bettingSide.sportsbook.sportsbookFeedKey;
                if (!sportsbookToWin[sportsbookKey]) {
                    sportsbookToWin[sportsbookKey] = new SportsbookComparisonItemVm(bettingSide.sportsbook, 0);
                }
            });
        });

        // Calculate total toWin (profit) amounts for parlay
        Object.keys(sportsbookToWin).forEach(sportsbookKey => {
            let parlayOddsDecimal = 1.0; // Start with 1 (neutral for multiplication)
            let validParlay = true;

            bets.forEach((bet) => {
                const bettingSide = bet.comparisonBets.find(bs => bs.sportsbook.sportsbookFeedKey === sportsbookKey);
                if (bettingSide) {
                    const oddsDecimal = parseFloat(String(bettingSide.oddsDecimal));
                    if (isNaN(oddsDecimal)) {
                        console.error(`Invalid odds value for betting side: ${bettingSide.oddsDecimal}`);
                        validParlay = false;
                    } else {
                        parlayOddsDecimal *= oddsDecimal;
                    }
                } else {
                    validParlay = false;
                }
            });

            if (validParlay) {
                const payout = parlayOddsDecimal * parlayWager;
                const toWin = payout - parlayWager; // Calculate profit
                sportsbookToWin[sportsbookKey].toWin = parseFloat(toWin.toFixed(2)); // Fix to two decimal places
                sportsbookToWin[sportsbookKey].odds = decimalToAmerican(parlayOddsDecimal);
            }
        });

        // Convert to sorted array
        return Object.values(sportsbookToWin).sort((a, b) => b.toWin - a.toWin);
    }

    private findBestSelectedParlay(bets: BetVm[], sortedComparisonList: SportsbookComparisonItemVm[]): SportsbookComparisonItemVm | null {
        // Extract the list of selected sportsbooks from the bets
        const selectedSportsbookKeys = new Set<string>();
        bets.forEach((bet) => {
            if (bet.selectedBet) {
                selectedSportsbookKeys.add(bet.selectedBet.sportsbook.sportsbookFeedKey);
            }
        });

        // Filter the sorted comparison list to include only selected sportsbooks
        const selectedComparisonList = sortedComparisonList.filter(item => selectedSportsbookKeys.has(item.sportsbookVm.sportsbookFeedKey));

        // Return the highest toWin value from the selected sportsbooks, if any
        return selectedComparisonList.length > 0 ? selectedComparisonList[0] : null;
    }

    private generateParlayDeepLink(bets: BetVm[], providerBookId: string): string | null {
        const marketValues = bets
            .flatMap(bet => bet.comparisonBets)
            .filter(bettingSide => bettingSide.sportsbook.providerBookId === providerBookId)
            .map(bettingSide => bettingSide.marketValue)
            .filter((value): value is string => value !== null);

        if (!providerBookId || marketValues.length === 0) {
            return null;
        }

        const marketSelection = marketValues.join(',');
        return `${deepLinkUrl}${providerBookId}?marketSelection=${marketSelection}`;
    }

    public getSortedComparisonList(): SportsbookComparisonItemVm[] {
        return this.sortedComparisonList;
    }

    public getBestSelectedParlay(): SportsbookComparisonItemVm | null {
        return this.bestSelectedParlay;
    }
}
